/**
 * Defines the main absence types used throughout the absence module.
 * These types categorize different kinds of employee absences and work arrangements.
 *
 * @enum {string}
 */
export const ABSENCE_TYPES = {
  VACATION: 'vacation',
  INCAPACITY: 'incapacity',
  BUSINESS_TRIP: 'businessTrip',
  HOME_OFFICE: 'homeOffice',
  WORKING_FROM_OFFICE: 'workingFromOffice',
};

/**
 * Defines the specific subtypes for absences, providing granular categorization
 * of different leave types, vacation types, and special circumstances.
 *
 * @enum {string}
 */
export const ABSENCE_SUBTYPES = {
  REGULAR_VACATION_LEAVE: '1_regularvacation',
  SAINT_GOBAIN_VACATION: 'PUHKUS',
  WINTER_VACATION_LEAVE: '2_wintervacation',
  STUDY_LEAVE: '2_study',
  FATHER_LEAVE: '3_father',
  BIRTH_LEAVE: '3_birth_leave',
  PARENT_LEAVE: '3_parent_leave',
  CHILD_LEAVE: '4_child',
  DISABLED_LEAVE: '5_disabled',
  PAYLESS_CHILD_LEAVE: '7_paylesschild',
  PAYLESS_LEAVE: '8_payless',
  EXTENDED_LEAVE: '9_extended',
  CARER_LEAVE: '10_carerleave',
  NATIONAL_SERVICE_LEAVE: '11_nationalservice',
  SENIORITY_LEAVE: '12_seniority',
  EXTRA_VACATION_LEAVE: '12_extra_vacation',
  HEALTH_DAY_LEAVE: '13_healthDay',
  OTHER_PAID_LEAVE: '13_otherpaid',
  RESERVIST_TRAINING_LEAVE: '17_reservist_training',
  SICK_LEAVE: '1_sickleave',
  MAINTENANCE_LEAVE: '2_maintenance_leave',
};

/**
 * Defines the absecnes status codes.
 * These statuses track the lifecycle of an absence from planning through approval.
 *
 * @enum {string}
 */
export const ABSENCE_STATUSES = {
  PENDING: 'pending',
  PLANNED: 'planned',
  APPROVED: 'approved',
  CANCELLED: 'cancelled',
  IN_CHANGE: 'inchange',
  IN_PLANNING: 'inplanning',
  MANAGER_IN_PLANNING: 'managerInplanning',
  MANAGER_APPROVAL: 'managerApproval',
  PRE_VACATION_CONFIRMATION: 'preVacationConfirmation',
};

/**
 * Defines the colour codes used to visually represent different absence types
 * in the holiday planner calendar and related UI components.
 *
 * @enum {string}
 */
export const ABSENCE_COLORS = {
  toovoimetus: '#9FACD8',
  kodukontor: '#FFCE20',
  puhkus: '#EB7778',
  lahetus: '#70CA44',
  koolitused: '#22C1E3',
  kontor: '#688AFF',
};

export type AbsenceType = {
  id: string;
  subtypes: string[];
}

type Form = {
  const?: string;
  allOf?: Form[];
  oneOf?: Form[];
  properties?: {
    [name: string]: Form;
  }
}

export function getAbsenceTypesFromForm(form: Form) {
  const result: AbsenceType[] = [];
  visitAbsenceTypes(form, result);
  return result;
}

function visitAbsenceTypes(form: Form, result: AbsenceType[]) {
  const absenceType = form.properties?.absenceType?.const;
  if (absenceType) {
    const subtypes: string[] = [];
    result.push({
      id: absenceType,
      subtypes,
    });
    visitSubtypes(form, subtypes);
  }
  if (form.oneOf) {
    for (const f of form.oneOf) {
      visitAbsenceTypes(f, result);
    }
  }
  if (form.allOf) {
    for (const f of form.allOf) {
      visitAbsenceTypes(f, result);
    }
  }
}

function visitSubtypes(form: Form, subtypes: string[]) {
  const subtype = form.properties?.subType?.const;
  if (subtype) {
    subtypes.push(subtype);
  }
  if (form.oneOf) {
    for (const f of form.oneOf) {
      visitSubtypes(f, subtypes);
    }
  }
  if (form.allOf) {
    for (const f of form.allOf) {
      visitSubtypes(f, subtypes);
    }
  }
}
